import { readClassRosterPermission } from '@/features/classes/roster/roster'
import { classSlots } from '@/features/classes/slots'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { shellSlots, type Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { Mail, Send } from '@/shared/ui/icons'

import {
  announcementRoute,
  announcementsPath,
  createAnnouncementPath,
  editAnnouncementRoute,
  readAnnouncementsPermission,
} from './announcements/paths'
import { emailStudentsPath, emailStudentsRoute } from './email-students/paths'
import { myDataApi } from './my-data/paths'
import { myMessageRoute, myMessagesPath } from './my-messages/paths'
import { createRulePath, editRuleRoute, readRulesPermission, ruleRoute, ruleSentMessagesRoute, rulesPath, writeRulesPermission } from './rules/paths'
import {
  classSentMessagesPath,
  classSentMessagesRoute,
  readSentMessagesPermission,
  sentMessageAdminScopes,
  sentMessageRoute,
  sentMessagesPath,
} from './sent-messages/paths'

/** Notifications: Notification Plans, sent notifications and announcements. Its screens register here. */
export const feature: Feature = {
  id: 'notifications',
  routes: [
    // My Messages and one message (NTF-24).
    {
      path: myMessagesPath,
      titleId: 'notifications.myMessages.title',
      page: () => import('./my-messages/MyMessagesPage').then((module) => module.MyMessagesPage),
    },
    {
      path: myMessageRoute,
      titleId: 'notifications.myMessages.eyebrow',
      page: () => import('./my-messages/MyMessageDetailsPage').then((module) => module.MyMessageDetailsPage),
    },
    // Find, Details, Create and Edit Announcement (NTF-22).
    {
      path: announcementsPath,
      titleId: 'notifications.announcements.find.title',
      page: () => import('./announcements/FindAnnouncementsPage').then((module) => module.FindAnnouncementsPage),
    },
    {
      path: createAnnouncementPath,
      titleId: 'notifications.announcements.create.title',
      page: () => import('./announcements/AnnouncementFormPages').then((module) => module.CreateAnnouncementPage),
    },
    {
      path: announcementRoute,
      titleId: 'notifications.announcements.eyebrow',
      page: () => import('./announcements/AnnouncementDetailsPage').then((module) => module.AnnouncementDetailsPage),
    },
    {
      path: editAnnouncementRoute,
      titleId: 'notifications.announcements.edit.title',
      page: () => import('./announcements/AnnouncementFormPages').then((module) => module.EditAnnouncementPage),
    },
    // Find, Details, Create and Edit Notification Plan, and a plan's sent messages (NTF-06).
    {
      path: rulesPath,
      titleId: 'notifications.rules.find.title',
      page: () => import('./rules/FindRulesPage').then((module) => module.FindRulesPage),
    },
    {
      path: createRulePath,
      titleId: 'notifications.rules.create.title',
      page: () => import('./rules/RuleFormPages').then((module) => module.CreateRulePage),
    },
    {
      path: ruleRoute,
      titleId: 'notifications.rules.eyebrow',
      page: () => import('./rules/RuleDetailsPage').then((module) => module.RuleDetailsPage),
    },
    {
      path: editRuleRoute,
      titleId: 'notifications.rules.edit.title',
      page: () => import('./rules/RuleFormPages').then((module) => module.EditRulePage),
    },
    {
      path: ruleSentMessagesRoute,
      titleId: 'notifications.sentMessages.title',
      page: () => import('./rules/RuleSentMessagesPage').then((module) => module.RuleSentMessagesPage),
    },
    // Find Sent Notifications, a class's, and one message (NTF-17).
    {
      path: sentMessagesPath,
      titleId: 'notifications.sentMessages.title',
      page: () => import('./sent-messages/FindSentMessagesPage').then((module) => module.FindSentMessagesPage),
    },
    {
      path: classSentMessagesRoute,
      titleId: 'notifications.sentMessages.title',
      page: () => import('./sent-messages/FindSentMessagesPage').then((module) => module.ClassSentMessagesPage),
    },
    {
      path: sentMessageRoute,
      titleId: 'notifications.sentMessages.eyebrow',
      page: () => import('./sent-messages/SentMessageDetailsPage').then((module) => module.SentMessageDetailsPage),
    },
    {
      // Email Students (NTF-25).
      path: emailStudentsRoute,
      titleId: 'notifications.emailStudents.title',
      page: () => import('./email-students/EmailStudentsPage').then((module) => module.EmailStudentsPage),
    },
  ],
  adminHome: {
    links: [
      {
        id: 'find-announcements',
        category: 'announcements',
        labelId: 'notifications.announcements.find.title',
        path: announcementsPath,
        permission: readAnnouncementsPermission,
      },
      {
        id: 'find-notification-plans',
        category: 'notifications',
        labelId: 'notifications.rules.find.title',
        path: rulesPath,
        permission: readRulesPermission,
      },
      {
        id: 'create-notification-plan',
        category: 'notifications',
        labelId: 'notifications.rules.create.title',
        path: createRulePath,
        permission: writeRulesPermission,
      },
      {
        // Everyone holds the read for their own messages, so the link asks for more.
        id: 'find-sent-messages',
        category: 'notifications',
        labelId: 'notifications.sentMessages.title',
        path: sentMessagesPath,
        permission: readSentMessagesPermission,
        permissionScope: sentMessageAdminScopes,
      },
    ],
  },
  slots: [
    // Notifications' part of "Download my data" on My Profile (NTF-29).
    { kind: 'link', slot: myDataSlot, id: 'notifications.my-data', labelId: 'notifications.myData.part', path: myDataApi },
    {
      // The `notifications` settings group on Create and Edit Organization (NTF-27).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'notifications',
      labelId: 'notifications.settings.label',
      component: () =>
        import('./organization-settings/NotificationSettingsSection').then((module) => module.NotificationSettingsSection),
    },
    {
      // The message center with the announcements shown to the caller, in the nav bar (NTF-23).
      kind: 'action',
      slot: shellSlots.headerActions,
      id: 'announcements',
      component: () => import('./announcements/AnnouncementCenter').then((module) => module.AnnouncementCenter),
    },
    {
      // The bell with the unread count, in the nav bar (NTF-24).
      kind: 'action',
      slot: shellSlots.headerActions,
      id: 'my-messages',
      component: () => import('./my-messages/MessagesBell').then((module) => module.MessagesBell),
    },
    {
      // Report a problem, first of the nav bar's actions, so left of Act on another site, while the BugReporting flag is on (111162).
      kind: 'action',
      slot: shellSlots.headerLeadingActions,
      id: 'report-problem',
      component: () => import('./bug-reports/ReportProblemButton').then((module) => module.ReportProblemButton),
    },
    {
      // Enable Notifications on the class form (NTF-11).
      kind: 'panel',
      slot: classSlots.formSections,
      id: 'notifications',
      component: () => import('./class-settings/ClassNotificationSwitch').then((module) => module.ClassNotificationSwitch),
    },
    {
      // Email Students in Class Details' sidebar, as legacy places it (NTF-25).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'email-students',
      labelId: 'notifications.emailStudents.sidebar',
      // Sending needs the class's roster, as the API's check does.
      permission: readClassRosterPermission,
      icon: Send,
      path: (context) => emailStudentsPath(context.recordId ?? ''),
    },
    {
      // Find Sent Notifications in Class Details' sidebar, filtered to the class (NTF-17).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'sent-messages',
      labelId: 'notifications.sentMessages.sidebar',
      permission: readSentMessagesPermission,
      icon: Mail,
      path: (context) => classSentMessagesPath(context.recordId ?? ''),
    },
  ],
}
